import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { Search, X } from "lucide-react";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  wrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    width: ({ fullwidth }) => (fullwidth ? "100%" : "inherit"),
  },
  icon: {
    position: "absolute",
    left: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    pointerEvents: "none",
    "& svg": { flexShrink: 0 },
  },
  input: {
    height: ({ size }) => (size === "small" ? 32 : size === "large" ? 48 : 40),
    width: "100%",
    border: "1px solid #DCDCDC",
    borderRadius: 4,
    outline: "none",
    // left pad for the search icon, right pad shrinks when no clear button
    padding: ({ hasClear, size }) => {
      const right = hasClear ? (size === "small" ? 28 : 36) : 12;
      const left = size === "small" ? 30 : 36;
      return `0 ${right}px 0 ${left}px`;
    },
    fontSize: ({ size }) => (size === "small" ? 13 : 16),
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    background: "transparent",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
    "&:focus": { borderColor: "var(--color-primary)" },
    "&:hover": { borderColor: "#BDBDBD" },
    "&::placeholder": { color: "#9ca3af" },
    "&:disabled": { background: "#f5f5f5", cursor: "not-allowed" },
  },
  clearBtn: {
    position: "absolute",
    right: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: ({ size }) => (size === "small" ? 20 : 24),
    height: ({ size }) => (size === "small" ? 20 : 24),
    border: "none",
    background: "none",
    cursor: "pointer",
    color: "#9ca3af",
    borderRadius: 4,
    padding: 0,
    transition: "all 0.15s",
    "&:hover": { background: "#f3f4f6", color: "#374151" },
  },
}));

const SearchInput = forwardRef(({ value, onChange, onClear, placeholder, size = "medium", fullwidth = true, name = "search", disabled = false, className, ...props }, ref) => {
  const hasClear = Boolean(value && onClear);
  const classes = useStyles({ fullwidth, size, hasClear });

  return (
    <div className={`${classes.wrap}${className ? ` ${className}` : ""}`}>
      <span className={classes.icon}>
        <Search size={size === "small" ? 13 : 16} />
      </span>
      <input
        ref={ref}
        name={name}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={classes.input}
        {...props}
      />
      {hasClear && (
        <button
          type="button"
          className={classes.clearBtn}
          onClick={onClear}
          tabIndex={-1}
          aria-label="Clear search"
        >
          <X size={size === "small" ? 11 : 14} />
        </button>
      )}
    </div>
  );
});

SearchInput.displayName = "SearchInput";

SearchInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  fullwidth: PropTypes.bool,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

export default SearchInput;
